import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiLink as LinkIcon } from 'react-icons/hi2';
import { Divider, Input, Label, Switch, Tabs, Tooltip } from '@knack/asterisk-react';

import { type BuilderPage } from '@/types/schema/BuilderPage';
import { usePagesQuery } from '@/hooks/api/queries/usePagesQuery';
import { getApplicationBasePathSegments, getPageAncestorsUrl } from '@/utils/application';
import { PAGE_NAME_MAX_CHAR_LENGTH } from '@/utils/constants';
import { formatSlug } from '@/utils/formatters';
import { CollapsiblePanel } from '@/components/layout/CollapsiblePanel';
import { usePageEditorContext } from '@/pages/pages/page-editor/PageEditorContext';

export function PageSettings() {
  const [t] = useTranslation();
  const { page, updatePage } = usePageEditorContext();
  const { data: allPages } = usePagesQuery();
  const { accountSlug, appSlug } = getApplicationBasePathSegments();
  const liveAppBasePath = `${import.meta.env.PUBLIC_LIVE_APP_URL}/${accountSlug}/${appSlug}`;
  const panelTitle =
    page.type === 'authentication'
      ? t('pages.login_page_settings.heading')
      : t('pages.settings.heading');

  // Keep local copy of the updated slug so we can update it on blur
  const [localSlug, setLocalSlug] = useState(page.slug);
  const urlWithAncestors = `${liveAppBasePath}${getPageAncestorsUrl(page, allPages)}/`;

  const handlePageUpdate = (pageUpdates: Partial<BuilderPage>) => {
    updatePage({
      type: 'page',
      action: 'update',
      pageUpdates
    });
  };

  const items = [
    {
      children: <span>{t('pages.settings.tabs.general')}</span>,
      value: 'general'
    },
    {
      children: (
        <Tooltip>
          <Tooltip.Trigger>{t('pages.settings.tabs.rules')}</Tooltip.Trigger>
          <Tooltip.Content>{t('keywords.coming_soon')}</Tooltip.Content>
        </Tooltip>
      ),
      value: 'rules',
      isDisabled: true
    },
    {
      children: (
        <Tooltip>
          <Tooltip.Trigger>{t('pages.settings.tabs.access')}</Tooltip.Trigger>
          <Tooltip.Content>{t('keywords.coming_soon')}</Tooltip.Content>
        </Tooltip>
      ),
      value: 'access',
      isDisabled: true
    }
  ];

  return (
    <CollapsiblePanel.Content title={panelTitle}>
      <Tabs defaultValue="general">
        <Tabs.List items={items} />
      </Tabs>
      <Divider className="my-4" />
      <Label>{t('pages.settings.page_name')}</Label>
      <Input
        className="mb-4"
        value={page.name}
        maxLength={PAGE_NAME_MAX_CHAR_LENGTH}
        onChange={(e) => {
          const newName = e.target.value;
          if (newName.length > PAGE_NAME_MAX_CHAR_LENGTH) return;

          handlePageUpdate({ name: newName });
        }}
      />
      <Label>{t('pages.settings.page_slug')}</Label>
      <Input
        value={localSlug}
        maxLength={PAGE_NAME_MAX_CHAR_LENGTH}
        onChange={(e) => {
          setLocalSlug(formatSlug(e.target.value));
        }}
        onBlur={() => handlePageUpdate({ slug: localSlug })}
      />
      <div className="mt-2 flex items-center rounded-lg bg-emphasis p-2">
        <LinkIcon className="shrink-0" />
        <a
          href={`${urlWithAncestors}${page.slug}`}
          target="_blank"
          className="ml-2 text-sm"
          style={{ overflowWrap: 'anywhere' }}
          rel="noreferrer"
        >
          {urlWithAncestors}
          <strong className="font-medium">{page.slug}</strong>
        </a>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Switch
          id="show-title"
          checked={page.isTitleVisible}
          onCheckedChange={(newValue) => {
            handlePageUpdate({ isTitleVisible: newValue });
          }}
        />
        <Label htmlFor="show-title">{t('pages.settings.show_title')}</Label>
      </div>
    </CollapsiblePanel.Content>
  );
}
